import "./LabDirectorDetailsEditComponent.scss";
import {Field, FieldProps, Form, Formik, FormikHelpers} from "formik";
import React, {useCallback, useEffect, useState} from "react";
import Grid from "@mui/material/Grid";
import FormikInputComponent from "../../../../../shared/components/core/formik-input/FormikInputComponent";
import CommonService from "../../../../../shared/services/common.service";
import ButtonComponent from "../../../../../shared/components/core/button/ButtonComponent";
import * as Yup from "yup";
import _ from "lodash";
import {IAPIResponseType} from "../../../../../shared/models/api.model";
import FromikPhoneNumberComponent
    from "../../../../../shared/components/core/fromik-phone-number/FromikPhoneNumberComponent";
import LinkComponent from "../../../../../shared/components/link/LinkComponent";
import commonService from "../../../../../shared/services/common.service";
import AttachmentViewDownloadComponent
    from "../../../../../shared/components/core/attachment-view-download/attachmentViewDownloadComponent";
import UploadOutlinedIcon from "@mui/icons-material/UploadOutlined";
import FeedbackComponent from "../../../../../shared/components/feedback/feedbackComponent";
import useAsyncState from "../../../../../shared/hooks/useAsyncState";
import {ImageConfig} from "../../../../../constants";
import FileDragAndDropComponent from "../../../../../shared/components/file-drag-n-drop/FileDragAndDropComponent";
import ChipComponent from "../../../../../shared/components/chip/ChipComponent";
import {AttachFile} from "@mui/icons-material";
import DialogComponent from "../../../../../shared/components/dialog/dialogComponent";

interface LabDirectorDetailsEditComponentProps {
    labDetails: any;
    onUpdateComplete: Function;
    labID: string | undefined;
    onEditCancel: Function;
    /**
     * Director details having two apis one is for basic details and another for attachments
     * This function is used to update the state after updating the attachments
     * @param labBasicDetails
     */
    handleLabDetailsUpdate: (labBasicDetails: any) => void;
}

const validationSchema = Yup.object({
    isSameAsAdmin: Yup.boolean().nullable(),
    npiNumber: Yup.string().nullable()
        .matches(/^[0-9]{10}$/, "NPI Number should be 10 digits"),
    firstName: Yup.string()
        .when('isSameAsAdmin', {
            is: false,
            then: Yup.string().nullable().required("First Name is required")
                .max(100, 'First name maximum limit should be 100 characters'),
        }),
    middleName: Yup.string()
        .when('isSameAsAdmin', {
            is: false,
            then: Yup.string().nullable()
                .max(100, 'Middle name maximum limit should be 100 characters'),
        }),
    lastName: Yup.string()
        .when('isSameAsAdmin', {
            is: false,
            then: Yup.string().nullable().required("Last Name is required")
                .max(100, 'Last name maximum limit should be 100 characters'),
        }),
    emailId: Yup.string()
        .when('isSameAsAdmin', {
            is: false,
            then: Yup.string().nullable().email("Invalid Email ID")
                .required("Email ID is required")
                .matches(/^[a-zA-Z0-9.!#$%&’*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/, "Enter a valid Email ID"),

        }),
    faxNumber: Yup.string().nullable()
        .when('isSameAsAdmin', {
            is: false,
            then: Yup.string().nullable()
                .matches(/^[(]{0,1}[0-9]{3}[)]{0,1}[-\s]{0,1}[0-9]{3}[-\s]{0,1}[0-9]{4}$/, "Fax Number should be 10 digits"),
        }),
    mobileNumber: Yup.string().nullable()
        .when('isSameAsAdmin', {
            is: false,
            then: Yup.string().nullable()
                .matches(/^[(]{0,1}[0-9]{3}[)]{0,1}[-\s]{0,1}[0-9]{3}[-\s]{0,1}[0-9]{4}$/, "Mobile Number should be 10 digits"),
        }),
    secondaryMobileNumber: Yup.string().nullable()
        .when('isSameAsAdmin', {
            is: false,
            then: Yup.string().nullable()
                .matches(/^[(]{0,1}[0-9]{3}[)]{0,1}[-\s]{0,1}[0-9]{3}[-\s]{0,1}[0-9]{4}$/, "Secondary Mobile Number should be 10 digits"),
        }),
});


interface CreateLabDirectorDetailsFormSchema {
    isSameAsAdmin?: boolean,
    npiNumber: string,
    firstName: string,
    middleName: string,
    lastName: string,
    mobileNumber: string,
    secondaryMobileNumber: string,
    faxNumber: string;
    emailId: string,
}

const formInitialValues: CreateLabDirectorDetailsFormSchema = {
    isSameAsAdmin: false,
    npiNumber: "",
    firstName: "",
    middleName: "",
    lastName: "",
    mobileNumber: "",
    secondaryMobileNumber: "",
    faxNumber: "",
    emailId: "",
}

const LabDirectorDetailsEditComponent = (props: LabDirectorDetailsEditComponentProps) => {
    const {onUpdateComplete, labID, onEditCancel, labDetails, handleLabDetailsUpdate} = props;
    const [labDirectorDetailsInitialValues, setLabDirectorDetailsInitialValues] = useState<CreateLabDirectorDetailsFormSchema>(_.cloneDeep(formInitialValues));
    const [isPhysicianDetailsLoading, setIsPhysicianDetailsLoading] = useState<boolean>(false);
    const [directorAttachments, setDirectorAttachments] = useState<any[]>([]);

    const onFormSubmit = useCallback((values: any, {setSubmitting, setErrors, validateForm}: FormikHelpers<any>) => {
        setSubmitting(true);
        const payload = {
            isSameAsAdmin: values['isSameAsAdmin'],
            directorDetails: {}
        };
        if (!values['isSameAsAdmin']) {
            payload["directorDetails"] = {
                npiNumber: values['npiNumber'],
                firstName: values['firstName'],
                middleName: values['middleName'],
                lastName: values['lastName'],
                mobileNumber: values['mobileNumber']?.replace(/[^0-9]/g, ''),
                secondaryMobileNumber: values['secondaryMobileNumber']?.replace(/[^0-9]/g, ''),
                faxNumber: values['faxNumber']?.replace(/[^0-9]/g, ''),
                emailId: values['emailId'],
                isSameAsAdmin: values['isSameAsAdmin'],
            }
        }
        CommonService._lab.editDirectorDetails(labID, payload)
            .then((response: IAPIResponseType<any>) => {
                setSubmitting(false);
                onUpdateComplete(response.data);
                CommonService._alert.showToast(response?.message || "Lab Director details has been edited", "success")
            })
            .catch((error: any) => {
                CommonService.handleErrors(setErrors, error);
                setSubmitting(false);
                CommonService._alert.showToast(error?.error || "Error while edit director details", "error")
            })

    }, [labID, onUpdateComplete]);

    const getNpiDetails = useCallback((npiNumber: string, resetForm: any) => {
        const payload = {'npiNumber': npiNumber}
        setIsPhysicianDetailsLoading(true)
        CommonService._facility.getPhysicainDetails(payload)
            .then((response: IAPIResponseType<any>) => {
                setIsPhysicianDetailsLoading(false)
                if (response?.data) {
                    const {
                        firstName,
                        lastName,
                        middleName,
                        mobileNumber,
                        emailId,
                        secondaryMobileNumber,
                        faxNumber,
                    } = response?.data;
                    resetForm({
                        values: {
                            ...labDirectorDetailsInitialValues,
                            isSameAsAdmin: false,
                            npiNumber: npiNumber,
                            firstName: firstName || '',
                            lastName: lastName || '',
                            middleName: middleName || '',
                            mobileNumber: commonService.getMaskedMobileNumber(mobileNumber || ''),
                            emailId: emailId || '',
                            secondaryMobileNumber: commonService.getMaskedMobileNumber(secondaryMobileNumber || ''),
                            faxNumber: commonService.getMaskedMobileNumber(faxNumber || ''),
                        }
                    })

                }
            })
            .catch((error: any) => {
                setIsPhysicianDetailsLoading(false)
                CommonService._alert.showToast(error?.error || "Failed to get Physician Details", "error");
            })

    }, [labDirectorDetailsInitialValues])

    const handlePopulateAdminDetails = useCallback((setFieldValue: (field: string, value: any, shouldValidate?: (boolean | undefined)) => void) => {
        if (!labDetails?.adminId) {
            CommonService._alert.showToast("Admin is not assigned to this lab", "warning");
            return;
        }
        const {
            firstName = '',
            middleName = '',
            lastName = '',
            mobileNumber = '',
            secondaryMobileNumber = '',
            faxNumber = '',
            emailId = ''
        } = labDetails?.adminId ? (labDetails?.adminDetails || {}) : {};

        setFieldValue('firstName', firstName || '')
        setFieldValue('lastName', lastName || '')
        setFieldValue('middleName', middleName || '')
        setFieldValue('mobileNumber', CommonService.getMaskedMobileNumber(mobileNumber || ''))
        setFieldValue('secondaryMobileNumber', CommonService.getMaskedMobileNumber(secondaryMobileNumber || ''))
        setFieldValue('faxNumber', CommonService.getMaskedMobileNumber(faxNumber || ''))
        setFieldValue('emailId', emailId || '')
        setFieldValue('npiNumber', '')
    }, [labDetails?.adminDetails, labDetails?.adminId])

    const [attachmentFile, setAttachmentFile] = useState<File[] | null>(null);
    const [isAttachmentDialogOpen, setIsAttachmentDialogOpen] = useState(false);

    const getUpdatedLabDetails = useCallback(() => {
        const payload = {};
        CommonService._lab.getLabDetails(labID, payload)
            .then((response: IAPIResponseType<any>) => {
                if (response?.data) {
                    handleLabDetailsUpdate(response.data);
                }
            })
            .catch((error: any) => {
            });
    }, [handleLabDetailsUpdate, labID])
    const handleRemoveFile = useCallback((file: any) => {
        const updatedFiles = attachmentFile ? attachmentFile.filter((fileItem: any) => fileItem.name !== file.name) : [];
        setAttachmentFile(updatedFiles)
    }, [attachmentFile])

    const {loading: isAttachmentUploading, execute} = useAsyncState();
    const addLabDirectorAttachments = useCallback(() => {
        const formData = new FormData();
        // Append the attachmentType
        formData.append('attachmentType', 'directorAttachments');
        // Append the files
        attachmentFile?.forEach((file: File) => {
            formData.append('files', file);
        });
        execute(() => CommonService._lab.addDirectorAttachments(labID || '', formData)).then((response: any) => {
            setDirectorAttachments((prev) => [...prev, ...(response?.data?.results || [])])
            setAttachmentFile(null);
            setIsAttachmentDialogOpen(false);
            getUpdatedLabDetails()
            CommonService._alert.showToast(response?.message || "Director attachments added", "success")
        }).catch((error) => {
            CommonService._alert.showToast(error?.error || "Error while adding director attachments", "error")
        })
    }, [attachmentFile, execute, getUpdatedLabDetails, labID])

    const {loading: isAttachmentDeleting, execute: deleteAttachment} = useAsyncState();
    const removeLabAttachment = useCallback((attachmentId: any) => {
        const payload = {};
        const id = CommonService._alert.loadingToast("Deleting director attachment");
        deleteAttachment(async () => {
            return CommonService._lab.deleteLabAttachment(labID, attachmentId, payload)
                .then((response: IAPIResponseType<any>) => {
                    // setIsEdited(true);
                    setDirectorAttachments((oldState: any) => {
                        const updatedList = _.cloneDeep(oldState);
                        const index = updatedList.findIndex((item: any) => item.id === attachmentId);
                        updatedList.splice(index, 1);
                        return updatedList;
                    })
                    getUpdatedLabDetails();
                    CommonService._alert.updateToast(id, response.message || 'Deleted successfully', 'success');
                })
                .catch((error: any) => {
                    CommonService._alert.updateToast(id, error?.message || 'Error deleting lab attachment', "error");
                })
        })
    }, [deleteAttachment, getUpdatedLabDetails, labID]);

    const handleAttachmentRemove = useCallback((id: any, type: any) => {
        CommonService.openConfirmationDialog({
            confirmationTitle: 'Please confirm!',
            confirmationSubTitle: 'Are you sure you want to delete this attachment?',
            disableEscKeyClose: false,
            disableBackdropClose: true,
            imagePath: `${ImageConfig.DeleteImage}`,
            direction: "up",
            yes: {
                text: "Confirm",
                color: "primary"
            },
            no: {
                text: "Cancel",
                color: "primary"
            }
        })
            .then(() => {
                removeLabAttachment(id);
            })
            .catch(() => {
            })
    }, [removeLabAttachment]);

    useEffect(() => {
        const directorDetails = labDetails?.directorDetails;
        console.log('this is the payload', directorDetails)
        if (directorDetails) {
            const tempDirectorDetailsObj = _.cloneDeep(directorDetails);
            tempDirectorDetailsObj.isSameAsAdmin = false; //currently we are giving open fields for director details.
            if (tempDirectorDetailsObj?.isSameAsAdmin) {
                tempDirectorDetailsObj.npiNumber = "";
                tempDirectorDetailsObj.firstName = "";
                tempDirectorDetailsObj.lastName = "";
                tempDirectorDetailsObj.middleName = "";
                tempDirectorDetailsObj.emailId = "";
                tempDirectorDetailsObj.faxNumber = "";
                tempDirectorDetailsObj.mobileNumber = "";
                tempDirectorDetailsObj.secondaryMobileNumber = "";
            } else {
                tempDirectorDetailsObj.faxNumber = CommonService.getMaskedMobileNumber(directorDetails?.faxNumber);
                tempDirectorDetailsObj.mobileNumber = CommonService.getMaskedMobileNumber(directorDetails?.mobileNumber);
                tempDirectorDetailsObj.secondaryMobileNumber = CommonService.getMaskedMobileNumber(directorDetails.mobileNumber);
            }
            setLabDirectorDetailsInitialValues(_.cloneDeep(tempDirectorDetailsObj));
        }
        //set the director attachments
        const directorAttachments: any[] = labDetails?.directorDetails?.attachments || [];
        if (directorAttachments?.length > 0) {
            setDirectorAttachments(directorAttachments);
        }
    }, [labDetails?.directorDetails]);

    return (
        <div className="lab-information-component details-tab-data-content">
            <Formik
                validationSchema={validationSchema}
                initialValues={labDirectorDetailsInitialValues}
                validateOnChange={true}
                validateOnBlur={true}
                enableReinitialize={true}
                validateOnMount={true}
                onSubmit={onFormSubmit}
            >
                {({
                      isSubmitting,
                      values,
                      resetForm,
                      isValid,
                      setFieldValue,
                      validateForm,
                      dirty,
                      touched,
                      setTouched
                  }) => {
                    // eslint-disable-next-line react-hooks/rules-of-hooks
                    useEffect(() => {
                        validateForm();
                    }, [validateForm, values]);
                    return (
                        <Form className={"lab-director-details-holder"}>
                            <div className={'t-form-controls-wrapper'}>
                                <div className="d-flex align-items-center mrg-bottom-10">
                                    <div className="redirect-new-link-wrapper mrg-bottom-20">
                                        <div>Fetch details of admin?</div>
                                        <LinkComponent
                                            className={"redirect-link"}
                                            onClick={() => handlePopulateAdminDetails(setFieldValue)}>
                                            Fetch Admin Details
                                        </LinkComponent>
                                    </div>
                                    {/*<div className="mrg-right-30 radio-button-title">Is the admin overseeing*/}
                                    {/*    this*/}
                                    {/*    role?*/}
                                    {/*</div>*/}
                                    {/*<Field*/}
                                    {/*    name={'isSameAsAdmin'}*/}
                                    {/*>*/}
                                    {/*    {*/}
                                    {/*        (field: FieldProps) => (*/}
                                    {/*            <FormikRadioButtonGroupComponent*/}
                                    {/*                direction={"row"}*/}
                                    {/*                isValueBoolean={true}*/}
                                    {/*                titleKey={'title'}*/}
                                    {/*                valueKey={'code'}*/}
                                    {/*                options={CommonService._staticData.yesNoOptions}*/}
                                    {/*                formikField={field}*/}
                                    {/*            />*/}
                                    {/*        )*/}
                                    {/*    }*/}
                                    {/*</Field>*/}
                                </div>
                                <>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={12} md={6} xl={6}>
                                            <Field name={'npiNumber'}>
                                                {
                                                    (field: FieldProps) => (
                                                        <FormikInputComponent
                                                            label={'NPI Number'}
                                                            placeholder={'Enter NPI Number'}
                                                            type={"text"}
                                                            required={false}
                                                            maxLength={10}
                                                            formikField={field}
                                                        />
                                                    )
                                                }
                                            </Field>
                                        </Grid>
                                        <Grid item xs={12} sm={12} md={6} xl={6} className={'pdd-top-25'}>
                                            <ButtonComponent
                                                type="button"
                                                color={"secondary"}
                                                variant={"outlined"}
                                                fullWidth={false}
                                                isLoading={isPhysicianDetailsLoading}
                                                handleClick={() => {
                                                    getNpiDetails(values.npiNumber, resetForm);
                                                }}
                                            >
                                                Fetch Details
                                            </ButtonComponent>
                                        </Grid>
                                    </Grid>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={12} md={6} xl={6}>
                                            <Field name={'firstName'}>
                                                {
                                                    (field: FieldProps) => (
                                                        <FormikInputComponent
                                                            label={'First Name'}
                                                            placeholder={'Enter First Name'}
                                                            type={"text"}
                                                            required={true}
                                                            formikField={field}
                                                        />
                                                    )
                                                }
                                            </Field>
                                        </Grid>
                                        <Grid item xs={12} sm={12} md={6} xl={6}>
                                            <Field name={'middleName'}>
                                                {
                                                    (field: FieldProps) => (
                                                        <FormikInputComponent
                                                            label={'Middle Name'}
                                                            placeholder={'Enter Middle Name'}
                                                            type={"text"}
                                                            required={false}
                                                            formikField={field}
                                                        />
                                                    )
                                                }
                                            </Field>
                                        </Grid>
                                    </Grid>
                                    <Grid container spacing={2}>

                                        <Grid item xs={12} sm={12} md={6} xl={6}>
                                            <Field name={'lastName'}>
                                                {
                                                    (field: FieldProps) => (
                                                        <FormikInputComponent
                                                            label={'Last Name'}
                                                            placeholder={'Enter Last Name'}
                                                            type={"text"}
                                                            required={true}
                                                            formikField={field}
                                                        />
                                                    )
                                                }
                                            </Field>

                                        </Grid>
                                        <Grid item xs={12} sm={12} md={6} xl={6}>
                                            <Field name={'emailId'}>
                                                {
                                                    (field: FieldProps) => (
                                                        <FormikInputComponent
                                                            label={'Email ID'}
                                                            placeholder={'Enter Email ID'}
                                                            type={"email"}
                                                            required={true}
                                                            formikField={field}
                                                        />
                                                    )
                                                }
                                            </Field>
                                        </Grid>
                                    </Grid>

                                    {/*<Grid container spacing={2}>*/}
                                    {/*    <Grid item xs={12} sm={12} md={6} xl={6}>*/}
                                    {/*        <Field name={'gender'}>*/}
                                    {/*            {*/}
                                    {/*                (field: FieldProps) => (*/}
                                    {/*                    <FormikSelectDropdownComponent*/}
                                    {/*                        label={'Gender'}*/}
                                    {/*                        options={CommonService._staticData.genderOptions}*/}
                                    {/*                        placeholder={'Select Gender'}*/}
                                    {/*                        valueKey={'code'}*/}
                                    {/*                        sx={{*/}
                                    {/*                            "& .MuiOutlinedInput-root": {*/}
                                    {/*                                background: "#FAFAFA"*/}
                                    {/*                            }*/}
                                    {/*                        }*/}
                                    {/*                        }*/}
                                    {/*                        required={true}*/}
                                    {/*                        getOptionLabel={(option) => (option.title)}*/}
                                    {/*                        formikField={field}/>*/}
                                    {/*                )*/}
                                    {/*            }*/}
                                    {/*        </Field>*/}
                                    {/*    </Grid>*/}
                                    {/*    <Grid item xs={12} sm={12} md={6} xl={6}>*/}
                                    {/*        <Field name={'dateOfBirth'}>*/}
                                    {/*            {*/}
                                    {/*                (field: FieldProps) => (*/}
                                    {/*                    <FormikDatePickerComponent*/}
                                    {/*                        label={'Date of Birth'}*/}
                                    {/*                        formikField={field}*/}
                                    {/*                        required={true}*/}
                                    {/*                        maxDate={formValidationRules.today}*/}
                                    {/*                        placeholder={'Enter Date of Birth'}*/}
                                    {/*                    />*/}
                                    {/*                )*/}
                                    {/*            }*/}
                                    {/*        </Field>*/}
                                    {/*    </Grid>*/}
                                    {/*</Grid>*/}
                                    <Grid container spacing={2}>

                                        <Grid item xs={12} sm={12} md={6} xl={6}>
                                            <Field name={'mobileNumber'}>
                                                {
                                                    (field: FieldProps) => (
                                                        <FromikPhoneNumberComponent
                                                            label={'Mobile Number'}
                                                            placeholder={'(000) 000-0000'}
                                                            maxLength={14}
                                                            required={true}
                                                            formikField={field}
                                                        />
                                                    )
                                                }
                                            </Field>
                                        </Grid>
                                        <Grid item xs={12} sm={12} md={6} xl={6}>
                                            <Field name={'secondaryMobileNumber'}>
                                                {
                                                    (field: FieldProps) => (
                                                        <FromikPhoneNumberComponent
                                                            label={'Secondary Mobile Number'}
                                                            placeholder={'(000) 000-0000'}
                                                            maxLength={14}
                                                            required={false}
                                                            formikField={field}
                                                        />
                                                    )
                                                }
                                            </Field>
                                        </Grid>
                                    </Grid>
                                    <Grid container spacing={2}>

                                        <Grid item xs={12} sm={12} md={6} xl={6}>
                                            <Field name={'faxNumber'}>
                                                {
                                                    (field: FieldProps) => (
                                                        <FromikPhoneNumberComponent
                                                            label={'Fax Number'}
                                                            placeholder={'(000) 000-0000'}
                                                            maxLength={14}
                                                            required={false}
                                                            formikField={field}
                                                        />
                                                    )
                                                }
                                            </Field>
                                        </Grid>
                                    </Grid>
                                </>
                                <>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={12} md={12} xl={12}>
                                            <div className="horizontal-line"/>
                                            <div className='form-sub-title'>Director Attachment(Recommended)</div>
                                            {directorAttachments?.length > 0 ?
                                                <>
                                                    <div className="attachment-list-wrapper">
                                                        {directorAttachments?.map((attachment: any) =>
                                                            <div key={CommonService.getUUID()}>
                                                                <AttachmentViewDownloadComponent
                                                                    attachmentTitle={attachment.attachmentName}
                                                                    attachment={attachment}
                                                                    showDeleteOption={!isAttachmentDeleting}
                                                                    onClick={(id: any) => handleAttachmentRemove(id, 'lab')}
                                                                />
                                                            </div>
                                                        )}
                                                    </div>
                                                </> :
                                                <>
                                                    <ButtonComponent
                                                        variant={"outlined"}
                                                        fullWidth={false}
                                                        color={"secondary"}
                                                        handleClick={() => {
                                                            setIsAttachmentDialogOpen(true)
                                                        }}
                                                        startIcon={<UploadOutlinedIcon/>}
                                                        type={'button'}>
                                                        Upload
                                                    </ButtonComponent>
                                                    <FeedbackComponent className={"mrg-top-25"}>
                                                        File type: JPG, PNG, PDF | Max. file size: 5MB
                                                    </FeedbackComponent>
                                                </>
                                            }
                                        </Grid>
                                    </Grid>
                                </>
                            </div>
                            <div className={"form-action-section-edit "}>
                                <ButtonComponent
                                    className={"width-25"}
                                    color={"secondary"}
                                    type={"button"}
                                    variant={"outlined"}
                                    disabled={isSubmitting || isPhysicianDetailsLoading}
                                    fullWidth={false}
                                    handleClick={() => {
                                        onEditCancel()
                                    }}
                                >
                                    Cancel
                                </ButtonComponent>
                                <ButtonComponent
                                    className={"width-25"}
                                    color={"primary"}
                                    type={"submit"}
                                    variant={"contained"}
                                    disabled={!(isValid && dirty) || isPhysicianDetailsLoading}
                                    fullWidth={false}
                                    isLoading={isSubmitting}
                                >
                                    Save
                                </ButtonComponent>
                            </div>
                        </Form>
                    )
                }
                }
            </Formik>
            <DialogComponent open={isAttachmentDialogOpen}
                             maxWidth={'xs'}
                             onClose={() => {
                                 setIsAttachmentDialogOpen(false)
                             }}
                             isShowCancel={true}
                             title={'Select Attachments'}
                             className={"attachment-preview-dialog"}>
                <>
                    <FileDragAndDropComponent
                        multiple={true}
                        maxFiles={1}
                        // title={"Attachments image/pdf"}
                        onFilesDrop={(value: any) => {
                            setAttachmentFile(value)
                        }}
                        acceptedFileTypes={{
                            'application/pdf': [],
                            'image/jpeg': [],
                            'image/png': [],
                        }}
                        fileSizeLimitText={"PNG, JPEG and PDF files are allowed upto 5MB"}
                    />
                    {
                        attachmentFile && attachmentFile?.map((file: any) => {
                            return <ChipComponent
                                label={file?.name}
                                onDelete={() => {
                                    handleRemoveFile(file);
                                }}
                                icon={<AttachFile/>}
                                className={'attachment-chip-item mrg-top-5'}
                            />
                        })
                    }
                    <div className="attachment-preview-dialog-footer">

                        <ButtonComponent variant={"outlined"}
                                         id="re_upload_btn"
                                         fullWidth={true}
                                         handleClick={() => {
                                             setIsAttachmentDialogOpen(false)
                                         }}
                        >
                            Cancel
                        </ButtonComponent>&nbsp;

                        <ButtonComponent variant={"contained"}
                                         id="continue_btn"
                                         fullWidth={true}
                                         isLoading={isAttachmentUploading}
                                         disabled={!Boolean(attachmentFile && attachmentFile?.length > 0)}
                                         handleClick={addLabDirectorAttachments}
                        >
                            Confirm Upload
                        </ButtonComponent>
                    </div>
                </>
            </DialogComponent>
        </div>

    );

};

export default LabDirectorDetailsEditComponent;
